import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Receipt, TrendingUp, PercentSquare, ClipboardList,
  Table2, Utensils, RefreshCw, ArrowUpRight, AlertCircle,
} from "lucide-react";
import { api, peso } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Today at a Glance — compact live snapshot above the Business Journey.
 *
 * - Real data only. Single aggregated call to `/api/dashboard/summary`
 *   (permission: `dashboard.view`). Respects the currently selected
 *   organization and branch context via the shared axios client.
 * - Cards derive from fields the backend already returns: gross_sales,
 *   net_sales (paid orders only), discounts_total, open_orders,
 *   occupied_tables / total_tables, open_kds_tickets.
 * - Loading = skeleton shimmer; error = per-section retry (single fetch);
 *   values are never faked. Refresh on mount, on branch change, on tab
 *   visibility change, and every 90s while the tab is visible.
 */

const REFRESH_MS = 90_000;

const METRICS = [
  {
    key: "sales_today",
    label: "Sales Today",
    testId: "glance-sales-today",
    icon: Receipt,
    accent: "sky",
    supporting: "Gross · excludes voids",
    to: "/reports",
    fmt: "peso",
    field: "gross_sales",
  },
  {
    key: "net_sales",
    label: "Net Sales",
    testId: "glance-net-sales",
    icon: TrendingUp,
    accent: "teal",
    supporting: "Paid orders only",
    to: "/reports",
    fmt: "peso",
    field: "net_sales",
  },
  {
    key: "discounts",
    label: "Discounts",
    testId: "glance-discounts",
    icon: PercentSquare,
    accent: "amber",
    supporting: "Applied today",
    to: "/reports/discounts",
    fmt: "peso",
    field: "discounts_total",
  },
  {
    key: "open_orders",
    label: "Open Orders",
    testId: "glance-open-orders",
    icon: ClipboardList,
    accent: "violet",
    supporting: "In progress",
    to: "/pos/orders",
    fmt: "int",
    field: "open_orders",
  },
  {
    key: "tables_in_use",
    label: "Tables In Use",
    testId: "glance-tables-in-use",
    icon: Table2,
    accent: "cyan",
    supporting: null,
    to: "/pos/tables",
    fmt: "ratio",
    field: "occupied_tables",
    field2: "total_tables",
  },
  {
    key: "kitchen_queue",
    label: "Kitchen Queue",
    testId: "glance-kitchen-queue",
    icon: Utensils,
    accent: "indigo",
    supporting: "Tickets awaiting prep",
    to: "/pos/kds",
    fmt: "int",
    field: "open_kds_tickets",
  },
];

const ACCENT = {
  sky:     { icon: "text-sky-300",     dot: "bg-sky-400",     glow: "shadow-[inset_0_1px_0_rgba(56,189,248,0.10)]" },
  teal:    { icon: "text-teal-300",    dot: "bg-teal-400",    glow: "shadow-[inset_0_1px_0_rgba(94,234,212,0.10)]" },
  amber:   { icon: "text-amber-300",   dot: "bg-amber-400",   glow: "shadow-[inset_0_1px_0_rgba(252,211,77,0.10)]" },
  violet:  { icon: "text-violet-300",  dot: "bg-violet-400",  glow: "shadow-[inset_0_1px_0_rgba(196,181,253,0.10)]" },
  cyan:    { icon: "text-cyan-300",    dot: "bg-cyan-400",    glow: "shadow-[inset_0_1px_0_rgba(103,232,249,0.10)]" },
  indigo:  { icon: "text-indigo-300",  dot: "bg-indigo-400",  glow: "shadow-[inset_0_1px_0_rgba(165,180,252,0.10)]" },
};

export default function TodayAtAGlance() {
  const { hasPerm, orgId, branchId } = useAuth();
  const canView = hasPerm("dashboard.view");

  // status: "idle" | "loading" | "ok" | "error"
  const [status, setStatus] = useState("idle");
  const [isRefetching, setIsRefetching] = useState(false);
  const [data, setData] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [asOf, setAsOf] = useState(null);
  const [tick, setTick] = useState(0); // forces "Xm ago" refresh

  const abortRef = useRef(null);
  const inflightIdRef = useRef(0);

  const load = useCallback(async () => {
    if (!canView || !orgId) return;
    // Cancel any in-flight request (branch change / manual refresh)
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const requestId = ++inflightIdRef.current;

    setStatus((prev) => (prev === "ok" ? "ok" : "loading"));
    setIsRefetching(true);
    setErrorMsg(null);
    try {
      const params = branchId ? { branch_id: branchId } : {};
      const { data: res } = await api.get("/dashboard/summary", {
        params,
        signal: controller.signal,
      });
      // Ignore stale responses (branch changed while in flight)
      if (requestId !== inflightIdRef.current) return;
      setData(res || {});
      setAsOf(res?.as_of || new Date().toISOString());
      setStatus("ok");
    } catch (err) {
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
      if (requestId !== inflightIdRef.current) return;
      setErrorMsg(
        err?.response?.data?.detail?.toString?.() ||
        err?.message ||
        "Could not load"
      );
      setStatus("error");
    } finally {
      if (requestId === inflightIdRef.current) setIsRefetching(false);
    }
  }, [canView, orgId, branchId]);

  // Fetch on mount + when org / branch changes; also clear data so users
  // never see a previous branch's numbers.
  useEffect(() => {
    if (!canView) return;
    setData(null);
    setStatus("loading");
    load();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView, orgId, branchId]);

  // Auto-refresh every REFRESH_MS while the tab is visible.
  useEffect(() => {
    if (!canView) return;
    let iv = null;
    const start = () => {
      stop();
      iv = setInterval(load, REFRESH_MS);
    };
    const stop = () => {
      if (iv) clearInterval(iv);
      iv = null;
    };
    const onVis = () => {
      if (document.visibilityState === "visible") {
        load();
        start();
      } else {
        stop();
      }
    };
    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [canView, load]);

  // Tick every 30s so "Xm ago" stays fresh even when data hasn't changed.
  useEffect(() => {
    const iv = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(iv);
  }, []);

  if (!canView) return null;

  const isLoading = status === "loading" && !data;
  const isError = status === "error" && !data;

  return (
    <section
      data-testid="today-at-a-glance"
      aria-label="Today at a Glance"
      className="relative"
    >
      {/* Section header */}
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <div className="min-w-0">
          <h2 className="text-[14px] font-semibold ink tracking-tight">
            Today at a Glance
          </h2>
          <p className="mt-0.5 text-[11.5px] ink-subtle">
            Live overview of today’s business activity.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <UpdatedLabel asOf={asOf} status={status} tick={tick} />
          <button
            type="button"
            onClick={load}
            disabled={isRefetching}
            data-testid="glance-refresh"
            aria-label="Refresh"
            title="Refresh"
            className="h-7 w-7 grid place-items-center rounded-md ink-muted hover:ink hover:bg-foreground/[0.06] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 disabled:opacity-60"
          >
            <RefreshCw
              size={12}
              className={isRefetching ? "animate-spin" : ""}
            />
          </button>
        </div>
      </div>

      {/* Section-level error only if we have no data at all */}
      {isError ? (
        <SectionError message={errorMsg} onRetry={load} />
      ) : (
        <div
          className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6"
          data-testid="glance-grid"
        >
          {METRICS.map((m) => (
            <MetricCard
              key={m.key}
              metric={m}
              loading={isLoading}
              data={data}
            />
          ))}
        </div>
      )}
    </section>
  );
}

/* --------------------------------------------------------------- Card */

function MetricCard({ metric, loading, data }) {
  const a = ACCENT[metric.accent] || ACCENT.sky;
  const Icon = metric.icon;

  const value = renderValue(metric, data);
  const hasValue = value !== null;

  const inner = (
    <>
      {/* Top row: icon + label + accent dot */}
      <div className="flex items-center gap-2">
        <span className="h-7 w-7 grid place-items-center rounded-md bg-white/[0.04] ring-1 ring-white/[0.06] shrink-0">
          <Icon size={13} className={a.icon} />
        </span>
        <span className="text-[11.5px] font-medium ink-muted tracking-wide truncate flex-1">
          {metric.label}
        </span>
        <span className={`h-1 w-1 rounded-full ${a.dot} opacity-70`} />
      </div>

      {/* Value */}
      <div className="mt-2.5 min-h-[30px] flex items-end">
        {loading ? (
          <span
            aria-hidden
            data-testid={`${metric.testId}-skeleton`}
            className="h-6 w-24 rounded-md bg-foreground/[0.08] animate-pulse"
          />
        ) : (
          <span className="text-[22px] leading-none font-semibold ink tracking-tight tabular-nums">
            {hasValue ? value : "—"}
          </span>
        )}
      </div>

      {/* Supporting text */}
      {metric.supporting && !loading && (
        <div className="mt-1 text-[10.5px] ink-subtle truncate">{metric.supporting}</div>
      )}

      {/* Arrow when actionable */}
      {metric.to && !loading && (
        <ArrowUpRight
          size={11}
          className="absolute top-3 right-3 ink-subtle opacity-0 group-hover:opacity-100 transition-opacity"
        />
      )}
    </>
  );

  const commonClass = `group relative block rounded-2xl border border-white/[0.06] bg-[linear-gradient(180deg,rgba(255,255,255,0.02),rgba(15,23,42,0.45))]
    p-4 min-h-[102px] transition-colors ${a.glow}
    hover:border-white/[0.16] hover:bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(30,41,59,0.55))]
    focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/40`;

  if (metric.to) {
    return (
      <Link
        to={metric.to}
        data-testid={metric.testId}
        aria-label={metric.label}
        className={commonClass}
      >
        {inner}
      </Link>
    );
  }
  return (
    <div data-testid={metric.testId} aria-label={metric.label} className={commonClass}>
      {inner}
    </div>
  );
}

function renderValue(metric, data) {
  if (!data) return null;
  const raw = data[metric.field];
  if (metric.fmt === "peso") {
    if (raw === null || raw === undefined) return null;
    return peso(raw);
  }
  if (metric.fmt === "ratio") {
    const num = data[metric.field];
    const den = data[metric.field2];
    if (num === undefined || den === undefined) return null;
    return `${num} / ${den}`;
  }
  // int
  if (raw === null || raw === undefined) return null;
  return Number(raw).toLocaleString("en-PH");
}

/* --------------------------------------------------------------- Bits */

function UpdatedLabel({ asOf, status, tick }) {
  if (status === "loading" && !asOf) {
    return <span className="text-[10.5px] ink-subtle">Loading…</span>;
  }
  if (!asOf) return null;
  const label = formatRelative(asOf, tick);
  return (
    <span
      data-testid="glance-updated"
      className="text-[10.5px] ink-subtle"
      title={new Date(asOf).toLocaleString()}
    >
      Updated {label}
    </span>
  );
}

function formatRelative(iso /* asOf ISO */, _tick /* used to force rerender */) {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.max(0, Math.round((now - then) / 1000));
  if (diffSec < 45) return "just now";
  const min = Math.round(diffSec / 60);
  if (min < 60) return `${min} min${min === 1 ? "" : "s"} ago`;
  const hr = Math.round(min / 60);
  return `${hr} hour${hr === 1 ? "" : "s"} ago`;
}

function SectionError({ message, onRetry }) {
  return (
    <div
      data-testid="glance-error"
      role="alert"
      className="rounded-2xl border border-amber-400/25 bg-amber-500/[0.05] px-4 py-3
        flex items-center gap-3"
    >
      <AlertCircle size={14} className="text-amber-300 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[12.5px] font-semibold ink">Could not load today’s snapshot</div>
        <div className="mt-0.5 text-[11px] ink-muted truncate">{message}</div>
      </div>
      <button
        type="button"
        onClick={onRetry}
        data-testid="glance-error-retry"
        className="h-7 px-3 rounded-md border hairline text-[11px] font-semibold ink-muted hover:ink hover:bg-foreground/[0.06] transition-colors"
      >
        Retry
      </button>
    </div>
  );
}
